<template>
  <div id="parceria-view">
    <div id="breadcrumb">
      <nav aria-label="breadcrumb">
        <ol class="breadcrumb">
          <li class="breadcrumb-item">
            <router-link to="/">Início</router-link>
          </li>

          <li class="breadcrumb-item">
            <router-link to="/parcerias">Parcerias</router-link>
          </li>

          <li
            class="breadcrumb-item active"
            aria-current="page"
          >
            Parceria Interna
          </li>
        </ol>
      </nav>
    </div>
    <div class="formulario-container">
      <div class="cabecalho">
        <div>
          <span class="tag"> FORMULÁRIO DE LEVANTAMENTO DE DEMANDA </span>

          <h1>Parceria Interna (Intra Muros) para Ações de Extensão</h1>

          <p>
            Este formulário deve ser preenchido por extensionistas
            coordenadores(as) de ações de extensão da UFC que desejam articular,
            com apoio da Coordenadoria de Articulação Social (PREX/Articula),
            uma parceria com outra unidade, centro, instituto ou campus da
            própria universidade (parceria intra muros).<br />

            As informações coletadas orientarão a aproximação entre as unidades
            e o apoio da PREX quando necessário.
          </p>

          <span class="tempo">
            <i class="fa-regular fa-clock"></i>

            Tempo estimado de preenchimento: 5 a 7 minutos.
          </span>
        </div>

        <img
          src="@/assets/brasaoUFC.png"
          alt="Logo PREX"
          class="logo"
        />
      </div>

      <!-- =================================================== -->
      <!-- CARREGANDO AÇÃO -->
      <!-- =================================================== -->

      <div
        v-if="carregandoAcao"
        class="aviso-carregando"
      >
        <i class="fa-solid fa-spinner fa-spin"></i>

        <div>
          <strong>Carregando dados da ação...</strong>

          <p>
            Estamos preenchendo previamente o formulário com as informações da
            ação de extensão.
          </p>
        </div>
      </div>

      <!-- =================================================== -->
      <!-- AÇÃO CARREGADA -->
      <!-- =================================================== -->

      <div
        v-if="acaoCarregada && !carregandoAcao && avisoAcaoVisivel"
        class="aviso-acao"
      >
        <i class="fa-solid fa-circle-info"></i>

        <div>
          <strong>Dados da ação carregados</strong>

          <p>
            Algumas informações foram preenchidas automaticamente com base nos
            dados cadastrados na ação. Você pode editar qualquer campo antes de
            enviar o formulário.
          </p>
        </div>

        <button
          type="button"
          aria-label="Fechar aviso"
          @click="fecharAvisoAcao"
        >
          ×
        </button>
      </div>

      <!-- =================================================== -->
      <!-- ERRO AO CARREGAR AÇÃO -->
      <!-- =================================================== -->

      <div
        v-if="erroCarregamentoAcao"
        class="aviso-erro"
      >
        <i class="fa-solid fa-circle-exclamation"></i>

        <div>
          <strong>Não foi possível carregar os dados da ação.</strong>

          <p>
            {{ erroCarregamentoAcao }}
          </p>
        </div>
      </div>
      <!-- =================================================== -->
      <!-- PROGRESSO -->
      <!-- =================================================== -->

      <div class="progresso-container">
        <div class="progresso-topo">
          <span> Etapa {{ etapaAtual }} de {{ totalEtapas }} </span>

          <span> {{ progresso }}% </span>
        </div>

        <div class="barra-progresso">
          <div
            class="barra-progresso-preenchida"
            :style="{ width: `${progresso}%` }"
          ></div>
        </div>

        <div class="etapas">
          <div
            v-for="etapa in totalEtapas"
            :key="etapa"
            class="etapa"
            :class="{
              ativa: etapa === etapaAtual,
              concluida: etapa < etapaAtual,
            }"
          >
            <span>
              {{ etapa }}
            </span>

            <small>
              {{ nomesEtapas[etapa - 1] }}
            </small>
          </div>
        </div>
      </div>

      <!-- =================================================== -->
      <!-- FORMULÁRIO -->
      <!-- =================================================== -->

      <form
        class="formulario"
        novalidate
        @submit.prevent="avancar"
      >
        <!-- ================================================= -->
        <!-- ETAPA 1 -->
        <!-- ================================================= -->

        <section v-if="etapaAtual === 1">
          <div class="secao-titulo">
            <span>SEÇÃO 1</span>

            <h2>Identificação do(a) coordenador(a) e da ação</h2>

            <p>
              Informe os dados do coordenador e da ação de extensão vinculada à
              solicitação.
            </p>
          </div>

          <!-- 1 - NOME -->

          <div class="campo">
            <label for="nome"> 1. Nome completo <b>*</b> </label>

            <input
              id="nome"
              v-model="form.nome"
              type="text"
              maxlength="150"
              autocomplete="name"
              placeholder="Digite seu nome completo"
              :class="{ erro: erros.nome }"
              @input="limparErro('nome')"
            />

            <span
              v-if="erros.nome"
              class="mensagem-erro"
            >
              {{ erros.nome }}
            </span>
          </div>

          <!-- 2 - SIAPE -->

          <div class="campo">
            <label for="siape"> 2. SIAPE / matrícula <b>*</b> </label>

            <input
              id="siape"
              v-model="form.siape"
              type="text"
              maxlength="30"
              inputmode="numeric"
              autocomplete="off"
              placeholder="Digite seu SIAPE ou matrícula"
              :class="{ erro: erros.siape }"
              @input="normalizarSiape"
            />

            <span
              v-if="erros.siape"
              class="mensagem-erro"
            >
              {{ erros.siape }}
            </span>
          </div>

          <!-- 3 - EMAIL -->

          <div class="campo">
            <label for="email"> 3. E-mail institucional <b>*</b> </label>

            <input
              id="email"
              v-model="form.email"
              type="email"
              maxlength="150"
              autocomplete="email"
              placeholder="exemplo@ufc.br"
              :class="{ erro: erros.email }"
              @input="limparErro('email')"
            />

            <span
              v-if="erros.email"
              class="mensagem-erro"
            >
              {{ erros.email }}
            </span>
          </div>

          <!-- 4 - TELEFONE -->

          <div class="campo">
            <label for="telefone">
              4. Telefone / WhatsApp para contato <b>*</b>
            </label>

            <input
              id="telefone"
              v-model="form.telefone"
              type="tel"
              maxlength="20"
              inputmode="tel"
              autocomplete="tel"
              placeholder="(88) 99999-9999"
              :class="{ erro: erros.telefone }"
              @input="aoDigitarTelefone"
            />

            <span
              v-if="erros.telefone"
              class="mensagem-erro"
            >
              {{ erros.telefone }}
            </span>
          </div>

          <!-- 5 - UNIDADE/CURSO -->

          <div class="campo">
            <label for="unidadeCurso">
              5. Unidade acadêmica de vinculação e Curso <b>*</b>
            </label>

            <input
              id="unidadeCurso"
              v-model="form.unidadeCurso"
              type="text"
              maxlength="200"
              placeholder="Ex.: Campus de Russas — Ciência da Computação"
              :class="{ erro: erros.unidadeCurso }"
              @input="limparErro('unidadeCurso')"
            />

            <span
              v-if="erros.unidadeCurso"
              class="mensagem-erro"
            >
              {{ erros.unidadeCurso }}
            </span>
          </div>

          <!-- 6 - CAMPUS -->

          <div class="campo">
            <label for="campus"> 6. Campus de origem <b>*</b> </label>

            <select
              id="campus"
              v-model="form.campus"
              :class="{ erro: erros.campus }"
              @change="limparErro('campus')"
            >
              <option value="">Selecione o campus</option>

              <option
                v-for="campus in opcoes.campi"
                :key="campus"
                :value="campus"
              >
                {{ campus }}
              </option>
            </select>

            <span
              v-if="erros.campus"
              class="mensagem-erro"
            >
              {{ erros.campus }}
            </span>
          </div>

          <!-- 7 - SIGAA -->

          <div class="campo">
            <label for="sigaa">
              7. Número da ação de extensão no SIGAA <b>*</b>
            </label>

            <input
              id="sigaa"
              v-model="form.sigaa"
              type="text"
              maxlength="30"
              inputmode="numeric"
              autocomplete="off"
              placeholder="Número da ação no SIGAA"
              :class="{ erro: erros.sigaa }"
              @input="normalizarSigaa"
            />

            <span
              v-if="erros.sigaa"
              class="mensagem-erro"
            >
              {{ erros.sigaa }}
            </span>
          </div>

          <!-- 8 - NOME DA AÇÃO -->

          <div class="campo">
            <label for="nomeAcao"> 8. Nome da ação de extensão <b>*</b> </label>

            <input
              id="nomeAcao"
              v-model="form.nomeAcao"
              type="text"
              maxlength="250"
              placeholder="Nome da ação de extensão"
              :class="{ erro: erros.nomeAcao }"
              @input="limparErro('nomeAcao')"
            />

            <span
              v-if="erros.nomeAcao"
              class="mensagem-erro"
            >
              {{ erros.nomeAcao }}
            </span>
          </div>

          <!-- 9 - TIPO DE AÇÃO -->

          <div class="campo">
            <label> 9. Qual é o tipo de ação de extensão? <b>*</b> </label>

            <p class="ajuda">
              Pode selecionar mais de uma opção, se necessário.
            </p>

            <div class="checkbox-list">
              <label
                v-for="tipo in opcoes.tiposAcao"
                :key="tipo"
                class="checkbox-item"
              >
                <input
                  v-model="form.tiposAcao"
                  type="checkbox"
                  :value="tipo"
                  @change="limparErro('tiposAcao')"
                />

                <span>
                  {{ tipo }}
                </span>
              </label>
            </div>

            <span
              v-if="erros.tiposAcao"
              class="mensagem-erro"
            >
              {{ erros.tiposAcao }}
            </span>
          </div>

          <!-- 10 - ÁREA TEMÁTICA -->

          <div class="campo">
            <label>
              10. Qual é a área temática principal da ação? <b>*</b>
            </label>

            <p class="ajuda">
              Conforme as áreas definidas na Resolução nº 05/CEPE, de 07 de
              março de 2025.
            </p>

            <div class="radio-list">
              <label
                v-for="area in opcoes.areasTematicas"
                :key="area"
                class="radio-item"
              >
                <input
                  v-model="form.areaTematica"
                  type="radio"
                  name="areaTematica"
                  :value="area"
                  @change="limparErro('areaTematica')"
                />

                <span>
                  {{ area }}
                </span>
              </label>
            </div>

            <span
              v-if="erros.areaTematica"
              class="mensagem-erro"
            >
              {{ erros.areaTematica }}
            </span>
          </div>
        </section>

        <!-- ================================================= -->
        <!-- ETAPA 2 -->
        <!-- ================================================= -->

        <section v-if="etapaAtual === 2">
          <div class="secao-titulo">
            <span>SEÇÃO 2</span>

            <h2>Sobre a parceria interna pretendida</h2>

            <p>
              Informe com qual campus ou unidade da UFC você deseja estabelecer
              a parceria.
            </p>
          </div>

          <!-- 11 -->

          <div class="campo">
            <label>
              11. Com qual(is) campus da UFC você deseja articular a parceria?
              <b>*</b>
            </label>

            <p class="ajuda">Pode selecionar mais de uma opção.</p>

            <div class="checkbox-list">
              <label
                v-for="campus in opcoes.campi"
                :key="campus"
                class="checkbox-item"
              >
                <input
                  v-model="form.campiParceiros"
                  type="checkbox"
                  :value="campus"
                  @change="limparErro('campiParceiros')"
                />

                <span>
                  {{ campus }}
                </span>
              </label>
            </div>

            <span
              v-if="erros.campiParceiros"
              class="mensagem-erro"
            >
              {{ erros.campiParceiros }}
            </span>
          </div>

          <!-- 12 -->

          <div class="campo">
            <label for="unidadeParceira">
              12. Com qual Centro, Instituto, Faculdade ou Curso específico, se
              já souber?
            </label>

            <input
              id="unidadeParceira"
              v-model="form.unidadeParceira"
              type="text"
              maxlength="250"
              placeholder="Ex.: Centro de Tecnologia, Instituto UFC Virtual, Curso de Engenharia..."
              :class="{ erro: erros.unidadeParceira }"
              @input="limparErro('unidadeParceira')"
            />

            <span
              v-if="erros.unidadeParceira"
              class="mensagem-erro"
            >
              {{ erros.unidadeParceira }}
            </span>
          </div>

          <!-- 13 -->

          <div class="campo">
            <label>
              13. Já possui contato com docente(s) ou coordenador(a) da unidade
              parceira? <b>*</b>
            </label>

            <div class="radio-list">
              <label
                v-for="opcao in opcoes.contatoUnidade"
                :key="opcao"
                class="radio-item"
              >
                <input
                  v-model="form.contatoUnidade"
                  type="radio"
                  name="contatoUnidade"
                  :value="opcao"
                  @change="limparErro('contatoUnidade')"
                />

                <span>
                  {{ opcao }}
                </span>
              </label>
            </div>

            <span
              v-if="erros.contatoUnidade"
              class="mensagem-erro"
            >
              {{ erros.contatoUnidade }}
            </span>
          </div>

          <!-- 14 -->

          <div class="campo">
            <label for="pessoaContato">
              14. Caso já tenha contato, informe nome, unidade e e-mail da
              pessoa
            </label>

            <textarea
              id="pessoaContato"
              v-model="form.pessoaContato"
              rows="5"
              maxlength="300"
              placeholder="Nome, unidade e e-mail da pessoa..."
              :class="{ erro: erros.pessoaContato }"
              @input="limparErro('pessoaContato')"
            ></textarea>

            <div class="contador">{{ form.pessoaContato.length }}/300</div>

            <span
              v-if="erros.pessoaContato"
              class="mensagem-erro"
            >
              {{ erros.pessoaContato }}
            </span>
          </div>

          <!-- 15 -->

          <div class="campo">
            <label>
              15. Que tipo de articulação você pretende estabelecer?
              <b>*</b>
            </label>

            <p class="ajuda">Pode selecionar mais de uma opção.</p>

            <div class="checkbox-list">
              <label
                v-for="opcao in opcoes.tiposArticulacao"
                :key="opcao"
                class="checkbox-item"
              >
                <input
                  v-model="form.tiposArticulacao"
                  type="checkbox"
                  :value="opcao"
                  @change="limparErro('tiposArticulacao')"
                />

                <span>
                  {{ opcao }}
                </span>
              </label>
            </div>

            <span
              v-if="erros.tiposArticulacao"
              class="mensagem-erro"
            >
              {{ erros.tiposArticulacao }}
            </span>
          </div>
        </section>

        <!-- ================================================= -->
        <!-- ETAPA 3 -->
        <!-- ================================================= -->

        <section v-if="etapaAtual === 3">
          <div class="secao-titulo">
            <span>SEÇÃO 3</span>

            <h2>Detalhamento da proposta</h2>

            <p>
              Explique o que está sendo proposto, o problema identificado e os
              benefícios esperados.
            </p>
          </div>

          <!-- 16 -->

          <div class="campo">
            <label for="proposta">
              16. Descreva a proposta de parceria interna <b>*</b>
            </label>

            <p class="ajuda">
              Explique de forma clara o que está sendo proposto e qual seria o
              papel de cada unidade envolvida.
            </p>

            <textarea
              id="proposta"
              v-model="form.proposta"
              rows="7"
              maxlength="3000"
              placeholder="Descreva a proposta de parceria..."
              :class="{ erro: erros.proposta }"
              @input="limparErro('proposta')"
            ></textarea>

            <div class="contador">{{ form.proposta.length }}/3000</div>

            <span
              v-if="erros.proposta"
              class="mensagem-erro"
            >
              {{ erros.proposta }}
            </span>
          </div>

          <!-- 17 -->

          <div class="campo">
            <label for="problema">
              17. Que problema, lacuna ou oportunidade essa articulação busca
              resolver? <b>*</b>
            </label>

            <textarea
              id="problema"
              v-model="form.problema"
              rows="6"
              maxlength="500"
              placeholder="Descreva o problema, lacuna ou oportunidade..."
              :class="{ erro: erros.problema }"
              @input="limparErro('problema')"
            ></textarea>

            <div class="contador">{{ form.problema.length }}/500</div>

            <span
              v-if="erros.problema"
              class="mensagem-erro"
            >
              {{ erros.problema }}
            </span>
          </div>

          <!-- 18 -->

          <div class="campo">
            <label for="beneficios">
              18. Quais benefícios mútuos são esperados para as unidades/campi
              envolvidos? <b>*</b>
            </label>

            <textarea
              id="beneficios"
              v-model="form.beneficios"
              rows="6"
              maxlength="500"
              placeholder="Descreva os benefícios esperados..."
              :class="{ erro: erros.beneficios }"
              @input="limparErro('beneficios')"
            ></textarea>

            <div class="contador">{{ form.beneficios.length }}/500</div>

            <span
              v-if="erros.beneficios"
              class="mensagem-erro"
            >
              {{ erros.beneficios }}
            </span>
          </div>

          <!-- 19 -->

          <div class="campo">
            <label for="beneficiarios">
              19. Quem são os beneficiários diretos e qual o número estimado?
              <b>*</b>
            </label>

            <textarea
              id="beneficiarios"
              v-model="form.beneficiarios"
              rows="5"
              maxlength="1500"
              placeholder="Descreva quem será beneficiado..."
              :class="{ erro: erros.beneficiarios }"
              @input="limparErro('beneficiarios')"
            ></textarea>

            <div class="contador">{{ form.beneficiarios.length }}/1500</div>

            <span
              v-if="erros.beneficiarios"
              class="mensagem-erro"
            >
              {{ erros.beneficiarios }}
            </span>

            <div class="radio-list">
              <label
                v-for="opcao in opcoes.quantidadeBeneficiarios"
                :key="opcao"
                class="radio-item"
              >
                <input
                  v-model="form.quantidadeBeneficiarios"
                  type="radio"
                  name="quantidadeBeneficiarios"
                  :value="opcao"
                  @change="limparErro('quantidadeBeneficiarios')"
                />

                <span>
                  {{ opcao }}
                </span>
              </label>
            </div>

            <span
              v-if="erros.quantidadeBeneficiarios"
              class="mensagem-erro"
            >
              {{ erros.quantidadeBeneficiarios }}
            </span>
          </div>

          <!-- 20 -->

          <div class="campo">
            <label>
              20. Qual é a abrangência territorial prevista para as ações?
              <b>*</b>
            </label>

            <div class="radio-list">
              <label
                v-for="opcao in opcoes.abrangencia"
                :key="opcao"
                class="radio-item"
              >
                <input
                  v-model="form.abrangencia"
                  type="radio"
                  name="abrangencia"
                  :value="opcao"
                  @change="limparErro('abrangencia')"
                />

                <span>
                  {{ opcao }}
                </span>
              </label>
            </div>

            <span
              v-if="erros.abrangencia"
              class="mensagem-erro"
            >
              {{ erros.abrangencia }}
            </span>
          </div>

          <!-- 21 -->

          <div class="campo">
            <label>
              21. A proposta beneficia diretamente grupos em situação de
              vulnerabilidade ou historicamente excluídos?
            </label>

            <p class="ajuda">Selecione todas as que se aplicam.</p>

            <div class="checkbox-list">
              <label
                v-for="grupo in opcoes.gruposVulnerabilidade"
                :key="grupo"
                class="checkbox-item"
              >
                <input
                  v-model="form.gruposVulnerabilidade"
                  type="checkbox"
                  :value="grupo"
                  @change="tratarNenhumGrupo"
                />

                <span>
                  {{ grupo }}
                </span>
              </label>
            </div>
          </div>
        </section>

        <!-- ================================================= -->
        <!-- ETAPA 4 -->
        <!-- ================================================= -->

        <section v-if="etapaAtual === 4">
          <div class="secao-titulo">
            <span>SEÇÃO 4</span>

            <h2>Duração e encerramento</h2>

            <p>
              Informe a duração prevista e revise as informações antes do envio.
            </p>
          </div>

          <!-- 22 -->

          <div class="campo">
            <label>
              22. Qual é a duração prevista das ações desta parceria?
              <b>*</b>
            </label>

            <div class="radio-list">
              <label
                v-for="opcao in opcoes.duracao"
                :key="opcao"
                class="radio-item"
              >
                <input
                  v-model="form.duracao"
                  type="radio"
                  name="duracao"
                  :value="opcao"
                  @change="limparErro('duracao')"
                />

                <span>
                  {{ opcao }}
                </span>
              </label>
            </div>

            <span
              v-if="erros.duracao"
              class="mensagem-erro"
            >
              {{ erros.duracao }}
            </span>
          </div>

          <!-- 23 -->

          <div class="campo">
            <label for="observacoes">
              23. Observações adicionais ou informações relevantes para a
              análise da PREX
            </label>

            <textarea
              id="observacoes"
              v-model="form.observacoes"
              rows="6"
              maxlength="2500"
              placeholder="Caso necessário, informe outras informações relevantes..."
              :class="{ erro: erros.observacoes }"
              @input="limparErro('observacoes')"
            ></textarea>

            <div class="contador">{{ form.observacoes.length }}/2500</div>

            <span
              v-if="erros.observacoes"
              class="mensagem-erro"
            >
              {{ erros.observacoes }}
            </span>
          </div>

          <!-- DECLARAÇÃO -->

          <div class="declaracao">
            <label class="checkbox-declaracao">
              <input
                v-model="form.declaracao"
                type="checkbox"
                @change="limparErro('declaracao')"
              />

              <span>
                Declaro que as informações prestadas são verídicas e estou
                ciente de que o registro constitui etapa de triagem preliminar,
                não configurando compromisso formal por parte da UFC. Também
                estou ciente de que os dados fornecidos serão utilizados
                exclusivamente para análise e articulação da parceria pela
                equipe da PREX/Articula.

                <b>*</b>
              </span>
            </label>

            <span
              v-if="erros.declaracao"
              class="mensagem-erro"
            >
              {{ erros.declaracao }}
            </span>
          </div>
        </section>

        <!-- ================================================= -->
        <!-- BOTÕES -->
        <!-- ================================================= -->

        <div class="acoes-formulario">
          <!-- VOLTAR -->

          <button
            v-if="etapaAtual > 1"
            type="button"
            class="btn-voltar"
            :disabled="enviandoFormulario"
            @click="voltar"
          >
            <i class="fa-solid fa-arrow-left"></i>

            Voltar
          </button>

          <!-- CONTINUAR -->

          <button
            v-if="etapaAtual < totalEtapas"
            type="submit"
            class="btn-continuar"
          >
            Continuar

            <i class="fa-solid fa-arrow-right"></i>
          </button>

          <!-- ENVIAR -->

          <button
            v-else
            type="button"
            class="btn-enviar"
            :disabled="enviandoFormulario"
            @click="enviarFormulario"
          >
            <i
              v-if="enviandoFormulario"
              class="fa-solid fa-spinner fa-spin"
            ></i>

            <i
              v-else
              class="fa-solid fa-paper-plane"
            ></i>

            {{ enviandoFormulario ? "Enviando..." : "Enviar formulário" }}
          </button>
        </div>
      </form>
    </div>
    <Aviso
      v-if="mostrarModal"
      :sucesso="modalSucesso"
      :texto="mensagemModal"
      @fecharModal="finalizarModal"
    />
  </div>
</template>

<script setup>
import { computed, onMounted, reactive, ref } from "vue";
import { useRoute, useRouter } from "vue-router";

import acoesService from "@/service/acaoService";
import parceriaService from "@/service/parceriaService";
import usuarioService from "@/service/usuariosService";
import Aviso from "@/components/common/aviso.vue";

const route = useRoute();
const router = useRouter();
const enviandoFormulario = ref(false);

const mostrarModal = ref(false);
const modalSucesso = ref(false);
const mensagemModal = ref("");

const etapaAtual = ref(1);
const totalEtapas = 4;
const nomesEtapas = ["Identificação", "Parceria", "Proposta", "Encerramento"];
const carregandoAcao = ref(false);
const acaoCarregada = ref(false);
const avisoAcaoVisivel = ref(true);
const erroCarregamentoAcao = ref("");

const form = reactive({
  nome: "",
  siape: "",
  email: "",
  telefone: "",
  unidadeCurso: "",
  campus: "",
  sigaa: "",
  nomeAcao: "",
  tiposAcao: [],
  areaTematica: "",

  campiParceiros: [],
  unidadeParceira: "",
  contatoUnidade: "",
  pessoaContato: "",
  tiposArticulacao: [],

  proposta: "",
  problema: "",
  beneficios: "",
  beneficiarios: "",
  quantidadeBeneficiarios: "",
  abrangencia: "",
  gruposVulnerabilidade: [],

  duracao: "",
  observacoes: "",

  declaracao: false,
});

const erros = reactive({});

const opcoes = {
  campi: [
    "Campus do Benfica (Fortaleza)",
    "Campus do Pici (Fortaleza)",
    "Campus do Porangabuçu (Fortaleza)",
    "Campus de Sobral",
    "Campus de Quixadá",
    "Campus de Russas",
    "Campus de Crateús",
    "Campus de Itapajé",
  ],

  tiposAcao: [
    "Projeto de extensão",
    "Programa de extensão",
    "Curso de extensão",
    "Evento de extensão",
    "ACCS — Ações Curriculares de Extensão",
    "Prestação de serviço especializado — serviços técnicos, laboratoriais, de consultoria, etc.",
  ],

  areasTematicas: [
    "Comunicação",
    "Cultura",
    "Direitos Humanos e Justiça",
    "Educação",
    "Meio Ambiente",
    "Saúde",
    "Tecnologia e Produção",
    "Trabalho",
  ],

  contatoUnidade: [
    "Sim, já tenho contato estabelecido",
    "Não, gostaria de indicação/apoio da PREX",
    "Estou em conversas preliminares",
  ],

  tiposArticulacao: [
    "Ação conjunta ou interdisciplinar de extensão",
    "Compartilhamento de infraestrutura, laboratórios ou espaços físicos",
    "Compartilhamento de equipe docente ou técnica",
    "Intercâmbio ou participação de estudantes de outro campus/unidade",
    "Realização conjunta de evento",
    "Integração com outro Programa ou Encontro de Extensão da UFC",
    "Outro",
  ],

  quantidadeBeneficiarios: [
    "Menos de 50 pessoas",
    "De 50 a 200 pessoas",
    "De 201 a 500 pessoas",
    "De 501 a 1.000 pessoas",
    "Mais de 1.000 pessoas",
    "Não é possível estimar",
  ],

  abrangencia: [
    "Local (bairro ou comunidade específica)",
    "Municipal (um município)",
    "Regional (mais de um município ou região)",
    "Estadual",
    "Múltiplos campi (capital e interior)",
  ],

  gruposVulnerabilidade: [
    "Pessoas em situação de pobreza ou vulnerabilidade socioeconômica",
    "Pessoas negras, quilombolas ou indígenas",
    "Mulheres em situação de vulnerabilidade",
    "Pessoas com deficiência",
    "Pessoas LGBTQIA+",
    "Povos e comunidades tradicionais (ribeirinhos, extrativistas, etc.)",
    "Populações rurais ou do semiárido",
    "Nenhum dos grupos acima",
  ],

  duracao: [
    "Até 3 meses",
    "De 3 a 6 meses",
    "De 6 meses a 1 ano",
    "Mais de 1 ano",
    "Sem prazo definido / Contínua",
  ],
};

const progresso = computed(() => {
  return Math.round((etapaAtual.value / totalEtapas) * 100);
});

function fecharAvisoAcao() {
  avisoAcaoVisivel.value = false;
}
function abrirModalSucesso(mensagem) {
  modalSucesso.value = true;
  mensagemModal.value = mensagem;
  mostrarModal.value = true;
}

function abrirModalErro(mensagem) {
  modalSucesso.value = false;
  mensagemModal.value = mensagem;
  mostrarModal.value = true;
}

function finalizarModal() {
  if (modalSucesso.value) {
    const acaoId = route.params.id;

    mostrarModal.value = false;

    router.push(`/acao/${acaoId}/parcerias`);

    return;
  }

  mostrarModal.value = false;
}

function textoValido(valor) {
  return typeof valor === "string" && valor.trim().length > 0;
}

function limparErros() {
  Object.keys(erros).forEach((campo) => {
    delete erros[campo];
  });
}

function limparErro(campo) {
  delete erros[campo];
}

async function carregarDadosAcao() {
  const acaoId = route.params.id;

  if (!acaoId) {
    console.warn("Nenhum ID de ação foi encontrado na rota.");
    return;
  }

  carregandoAcao.value = true;

  try {
    const acao = await acoesService.getAcoesById(acaoId);

    if (!acao) {
      console.warn("A API não retornou os dados da ação.");
      return;
    }

    preencherFormularioComAcao(acao);

    acaoCarregada.value = true;
  } catch (error) {
    console.error("Erro ao carregar dados da ação:", error);
  } finally {
    carregandoAcao.value = false;
  }
}

async function carregarDadosUsuarioLogado() {
  try {
    const usuario = await usuarioService.getUsuarioLogado();

    if (!usuario) {
      console.warn("A API não retornou os dados do usuário logado.");
      return;
    }

    if (textoValido(usuario.email)) {
      form.email = usuario.email.trim();
    }

    if (textoValido(usuario.telefone)) {
      form.telefone = formatarTelefone(usuario.telefone);
    }
  } catch (error) {
    console.error("Erro ao carregar usuário logado:", error);
  }
}

function preencherFormularioComAcao(acao) {
  if (textoValido(acao.nomeCoordenador)) {
    form.nome = limparAspasExcedentes(acao.nomeCoordenador);
  }
  if (textoValido(acao.titulo)) {
    form.nomeAcao = limparAspasExcedentes(acao.titulo);
  }
}

function limparAspasExcedentes(texto) {
  if (typeof texto !== "string") {
    return "";
  }

  return texto
    .trim()
    .replace(/^"+|"+$/g, "")
    .trim();
}

function normalizarSiape() {
  form.siape = form.siape.replace(/\D/g, "").slice(0, 30);

  limparErro("siape");
}

function normalizarSigaa() {
  form.sigaa = form.sigaa.replace(/\D/g, "").slice(0, 30);

  limparErro("sigaa");
}
function formatarTelefone(valor) {
  if (valor === null || valor === undefined) {
    return "";
  }

  let numeros = String(valor).replace(/\D/g, "");

  // Remove o código do Brasil quando vier como +55 ou 55.
  if (
    numeros.startsWith("55") &&
    (numeros.length === 12 || numeros.length === 13)
  ) {
    numeros = numeros.slice(2);
  }

  // Celular brasileiro: (88) 94002-7778
  if (numeros.length === 11) {
    return `(${numeros.slice(0, 2)}) ${numeros.slice(2, 7)}-${numeros.slice(7)}`;
  }

  // Telefone fixo: (88) 4002-7778
  if (numeros.length === 10) {
    return `(${numeros.slice(0, 2)}) ${numeros.slice(2, 6)}-${numeros.slice(6)}`;
  }

  // Durante a digitação
  if (numeros.length === 9) {
    return `${numeros.slice(0, 5)}-${numeros.slice(5)}`;
  }

  if (numeros.length === 8) {
    return `${numeros.slice(0, 4)}-${numeros.slice(4)}`;
  }

  return numeros;
}
function aoDigitarTelefone(event) {
  const valor = event?.target?.value ?? "";

  form.telefone = formatarTelefone(valor);

  limparErro("telefone");
}

function emailValido(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
}

function telefoneValido(telefone) {
  if (!textoValido(telefone)) {
    return false;
  }

  return /^\(\d{2}\) \d{4,5}-\d{4}$/.test(telefone.trim());
}

function validarEtapa1() {
  limparErros();

  if (!textoValido(form.nome)) {
    erros.nome = "Informe o nome completo.";
  } else if (form.nome.trim().length > 150) {
    erros.nome = "O nome deve ter no máximo 150 caracteres.";
  }

  if (!textoValido(form.siape)) {
    erros.siape = "Informe o SIAPE ou matrícula.";
  } else if (!/^\d+$/.test(form.siape)) {
    erros.siape = "O SIAPE/matrícula deve conter apenas números.";
  } else if (form.siape.length > 30) {
    erros.siape = "O SIAPE/matrícula deve ter no máximo 30 caracteres.";
  }

  if (!textoValido(form.email)) {
    erros.email = "Informe o e-mail institucional.";
  } else if (form.email.length > 150) {
    erros.email = "O e-mail deve ter no máximo 150 caracteres.";
  } else if (!emailValido(form.email)) {
    erros.email = "Informe um e-mail válido.";
  }

  if (!textoValido(form.telefone)) {
    erros.telefone = "Informe um telefone para contato.";
  } else if (!telefoneValido(form.telefone)) {
    erros.telefone = "Informe um telefone válido.";
  }

  if (!textoValido(form.unidadeCurso)) {
    erros.unidadeCurso = "Informe a unidade acadêmica e o curso.";
  } else if (form.unidadeCurso.trim().length > 200) {
    erros.unidadeCurso = "A unidade/curso deve ter no máximo 200 caracteres.";
  }

  if (!textoValido(form.campus)) {
    erros.campus = "Selecione o campus de origem.";
  } else if (form.campus.length > 255) {
    erros.campus = "O campus deve ter no máximo 255 caracteres.";
  }

  if (!textoValido(form.sigaa)) {
    erros.sigaa = "Informe o número da ação no SIGAA.";
  } else if (!/^\d+$/.test(form.sigaa)) {
    erros.sigaa = "O SIGAA deve conter apenas números.";
  } else if (form.sigaa.length > 30) {
    erros.sigaa = "O SIGAA deve ter no máximo 30 caracteres.";
  }

  if (!textoValido(form.nomeAcao)) {
    erros.nomeAcao = "Informe o nome da ação de extensão.";
  } else if (form.nomeAcao.trim().length > 250) {
    erros.nomeAcao = "O nome da ação deve ter no máximo 250 caracteres.";
  }

  if (!Array.isArray(form.tiposAcao) || form.tiposAcao.length < 1) {
    erros.tiposAcao = "Selecione pelo menos um tipo de ação.";
  }

  if (!textoValido(form.areaTematica)) {
    erros.areaTematica = "Selecione a área temática principal.";
  }

  return Object.keys(erros).length === 0;
}

function validarEtapa2() {
  limparErros();

  if (!Array.isArray(form.campiParceiros) || form.campiParceiros.length < 1) {
    erros.campiParceiros = "Selecione pelo menos um campus para a parceria.";
  }

  if (form.unidadeParceira && form.unidadeParceira.length > 250) {
    erros.unidadeParceira =
      "A unidade parceira deve ter no máximo 250 caracteres.";
  }

  if (!textoValido(form.contatoUnidade)) {
    erros.contatoUnidade = "Selecione uma opção.";
  }

  if (form.pessoaContato && form.pessoaContato.length > 300) {
    erros.pessoaContato =
      "As informações de contato devem ter no máximo 300 caracteres.";
  }

  if (
    !Array.isArray(form.tiposArticulacao) ||
    form.tiposArticulacao.length < 1
  ) {
    erros.tiposArticulacao = "Selecione pelo menos um tipo de articulação.";
  }

  return Object.keys(erros).length === 0;
}

function validarEtapa3() {
  limparErros();

  if (!textoValido(form.proposta)) {
    erros.proposta = "Descreva a proposta de parceria interna.";
  } else if (form.proposta.trim().length < 30) {
    erros.proposta =
      "A descrição da proposta deve ter pelo menos 30 caracteres.";
  } else if (form.proposta.trim().length > 3000) {
    erros.proposta = "A proposta deve ter no máximo 3000 caracteres.";
  }

  if (!textoValido(form.problema)) {
    erros.problema = "Informe o problema, lacuna ou oportunidade identificada.";
  } else if (form.problema.trim().length > 500) {
    erros.problema = "O problema deve ter no máximo 500 caracteres.";
  }

  if (!textoValido(form.beneficios)) {
    erros.beneficios = "Informe os benefícios mútuos esperados.";
  } else if (form.beneficios.trim().length > 500) {
    erros.beneficios = "Os benefícios devem ter no máximo 500 caracteres.";
  }

  if (!textoValido(form.beneficiarios)) {
    erros.beneficiarios = "Informe quem são os beneficiários diretos.";
  } else if (form.beneficiarios.trim().length > 1500) {
    erros.beneficiarios =
      "Os beneficiários devem ter no máximo 1500 caracteres.";
  }

  if (!textoValido(form.quantidadeBeneficiarios)) {
    erros.quantidadeBeneficiarios =
      "Selecione a quantidade estimada de beneficiários.";
  }

  if (!textoValido(form.abrangencia)) {
    erros.abrangencia = "Selecione a abrangência territorial.";
  }

  return Object.keys(erros).length === 0;
}

function validarEtapa4() {
  limparErros();

  if (!textoValido(form.duracao)) {
    erros.duracao = "Selecione a duração prevista.";
  }

  if (form.observacoes && form.observacoes.length > 2500) {
    erros.observacoes = "As observações devem ter no máximo 2500 caracteres.";
  }

  if (form.declaracao !== true) {
    erros.declaracao = "Você precisa aceitar a declaração para continuar.";
  }

  return Object.keys(erros).length === 0;
}

function validarEtapaAtual() {
  switch (etapaAtual.value) {
    case 1:
      return validarEtapa1();

    case 2:
      return validarEtapa2();

    case 3:
      return validarEtapa3();

    case 4:
      return validarEtapa4();

    default:
      return false;
  }
}

function avancar() {
  if (!validarEtapaAtual()) {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });

    return;
  }

  if (etapaAtual.value < totalEtapas) {
    etapaAtual.value++;

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }
}

function voltar() {
  if (etapaAtual.value > 1) {
    limparErros();

    etapaAtual.value--;

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }
}

function tratarNenhumGrupo() {
  const nenhum = "Nenhum dos grupos acima";

  if (
    form.gruposVulnerabilidade.includes(nenhum) &&
    form.gruposVulnerabilidade.length > 1
  ) {
    form.gruposVulnerabilidade = form.gruposVulnerabilidade.filter(
      (item) => item === nenhum
    );
  }
}

async function enviarFormulario() {
  if (enviandoFormulario.value) {
    return;
  }

  if (!validarEtapa4()) {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });

    return;
  }

  enviandoFormulario.value = true;

  const payload = {
    ...JSON.parse(JSON.stringify(form)),
    acaoExtensaoId: route.params.id,
    tipoParceria: "INTRA_MUROS",
  };

  try {
    const resposta = await parceriaService.criarParceria(payload);

    console.log("SUCESSO:", resposta);

    abrirModalSucesso("Parceria interna cadastrada com sucesso!");
  } catch (error) {
    console.error("Erro ao criar parceria:", error);

    let mensagem = "Não foi possível cadastrar a parceria. Tente novamente.";

    if (error.response?.data?.message) {
      mensagem = error.response.data.message;
    } else if (error.response?.data?.mensagem) {
      mensagem = error.response.data.mensagem;
    }

    abrirModalErro(mensagem);
  } finally {
    enviandoFormulario.value = false;
  }
}

onMounted(async () => {
  await Promise.all([carregarDadosAcao(), carregarDadosUsuarioLogado()]);
});
</script>
<style scoped>
#parceria-view {
  min-height: calc(100vh - 130px);
  padding: 32px 8%;
  background: #fff;
}

.formulario-container {
  max-width: 1000px;
  margin: 25px auto 0;
}

/* CABEÇALHO */

.cabecalho {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 40px;
  padding-bottom: 30px;
  border-bottom: 1px solid #e5e5e5;
}

.cabecalho > div {
  flex: 1;
}

.tag {
  display: inline-block;
  color: #3a2474;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.6px;
  margin-bottom: 10px;
}

.cabecalho h1 {
  color: #222;
  font-size: 28px;
  margin: 0 0 15px;
}

.cabecalho p {
  color: #666;
  line-height: 1.7;
  margin: 0 0 12px;
}

.tempo {
  display: inline-block;
  color: #777;
  font-size: 13px;
}

.tempo i {
  margin-right: 5px;
}

.logo {
  width: 180px;
  height: auto;
}

/* AVISO CARREGANDO */

.aviso-carregando {
  display: flex;
  align-items: flex-start;
  gap: 14px;

  margin-top: 25px;
  padding: 15px 18px;

  background: #f7f4ff;
  border-left: 4px solid #7c3aed;
  border-radius: 6px;

  color: #333;
}

.aviso-carregando > i {
  color: #7c3aed;
  font-size: 19px;
  margin-top: 2px;
}

.aviso-carregando div {
  flex: 1;
}

.aviso-carregando strong {
  color: #3a2474;
  font-size: 14px;
}

.aviso-carregando p {
  color: #666;
  font-size: 13px;
  margin: 4px 0 0;
}

/* AVISO AÇÃO */

.aviso-acao {
  display: flex;
  align-items: flex-start;
  gap: 14px;

  margin-top: 25px;
  padding: 15px 18px;

  background: #f7f4ff;
  border-left: 4px solid #3a2474;
  border-radius: 6px;

  color: #333;
}

.aviso-acao > i {
  color: #3a2474;
  font-size: 19px;
  margin-top: 2px;
}

.aviso-acao div {
  flex: 1;
}

.aviso-acao strong {
  color: #3a2474;
  font-size: 14px;
}

.aviso-acao p {
  color: #666;
  font-size: 13px;
  margin: 4px 0 0;
}

.aviso-acao button {
  border: none;
  background: transparent;
  color: #777;
  cursor: pointer;
  font-weight: bold;
}

/* RASCUNHO RESTAURADO */

.aviso-restaurado {
  display: flex;
  align-items: flex-start;
  gap: 14px;

  margin-top: 25px;
  padding: 15px 18px;

  background: #f7f4ff;
  border-left: 4px solid #3a2474;
  border-radius: 6px;

  color: #333;
}

.aviso-restaurado > i {
  color: #3a2474;
  font-size: 19px;
  margin-top: 2px;
}

.aviso-restaurado div {
  flex: 1;
}

.aviso-restaurado strong {
  color: #3a2474;
  font-size: 14px;
}

.aviso-restaurado p {
  color: #666;
  font-size: 13px;
  margin: 4px 0 0;
}

.aviso-restaurado button {
  border: none;
  background: transparent;
  color: #777;
  cursor: pointer;
  font-weight: bold;
}

/* PROGRESSO */

.progresso-container {
  margin: 30px 0;
}

.progresso-topo {
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;

  font-size: 13px;
  color: #666;
  font-weight: 600;
}

.barra-progresso {
  height: 7px;
  background: #e9e9e9;
  border-radius: 10px;
  overflow: hidden;
}

.barra-progresso-preenchida {
  height: 100%;
  background: #7c3aed;
  transition: width 0.3s ease;
}

.etapas {
  display: flex;
  justify-content: space-between;
  margin-top: 15px;
}

.etapa {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 5px;
  color: #999;
  text-align: center;
}

.etapa span {
  width: 28px;
  height: 28px;

  display: flex;
  align-items: center;
  justify-content: center;

  border-radius: 50%;
  background: #e5e5e5;

  font-size: 12px;
  font-weight: 700;
}

.etapa small {
  font-size: 11px;
}

.etapa.ativa {
  color: #3a2474;
}

.etapa.ativa span,
.etapa.concluida span {
  background: #3a2474;
  color: #fff;
}

/* FORMULÁRIO */

.formulario {
  background: #fff;

  border: 1px solid #e2e2e2;
  border-radius: 10px;

  padding: 32px;

  box-shadow: 0 3px 12px rgba(0, 0, 0, 0.06);
}

/* TÍTULO DA SEÇÃO */

.secao-titulo {
  margin-bottom: 30px;
  padding-bottom: 20px;
  border-bottom: 1px solid #eee;
}

.secao-titulo > span {
  color: #7c3aed;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.6px;
}

.secao-titulo h2 {
  color: #222;
  font-size: 21px;
  margin: 7px 0 8px;
}

.secao-titulo p {
  color: #777;
  font-size: 14px;
  margin: 0;
  line-height: 1.5;
}

/* CAMPOS */

.campo {
  position: relative;
  display: flex;
  flex-direction: column;
  margin-bottom: 25px;
}

.campo > label {
  color: #333;
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 7px;
}

.campo > label b {
  color: #d00000;
}

.campo input[type="text"],
.campo input[type="email"],
.campo input[type="tel"],
.campo select,
.campo textarea {
  width: 100%;
  box-sizing: border-box;

  border: 1px solid #ccc;
  border-radius: 7px;

  padding: 11px 13px;

  color: #333;
  background: #fff;

  font-size: 14px;
  font-family: inherit;

  outline: none;

  transition: border-color 0.15s ease;
}

.campo input:focus,
.campo select:focus,
.campo textarea:focus {
  border-color: #7c3aed;
}

.campo textarea {
  resize: vertical;
  min-height: 100px;
}

.campo input.erro,
.campo select.erro,
.campo textarea.erro {
  border-color: #d00000;
}

.ajuda {
  color: #777;
  font-size: 12px;
  line-height: 1.5;
  margin: -2px 0 10px;
}

.mensagem-erro {
  color: #d00000;
  font-size: 12px;
  margin-top: 5px;
}

.contador {
  align-self: flex-end;
  color: #999;
  font-size: 11px;
  margin-top: 4px;
}

/* RADIO E CHECKBOX */

.checkbox-list,
.radio-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.checkbox-item,
.radio-item {
  display: flex;
  align-items: flex-start;
  gap: 9px;

  padding: 10px 12px;

  border: 1px solid #eee;
  border-radius: 7px;

  cursor: pointer;

  font-size: 13px;
  color: #444;

  transition:
    background-color 0.15s ease,
    border-color 0.15s ease;
}

.checkbox-item:hover,
.radio-item:hover {
  background: #faf8ff;
  border-color: #d6c8f5;
}

.checkbox-item input,
.radio-item input {
  margin-top: 2px;
  accent-color: #7c3aed;
  cursor: pointer;
}

/* DECLARAÇÃO */

.declaracao {
  margin-top: 30px;
  padding: 18px;

  background: #f8f8f8;
  border-radius: 8px;

  border: 1px solid #e5e5e5;
}

.checkbox-declaracao {
  display: flex;
  align-items: flex-start;
  gap: 10px;

  color: #555;
  font-size: 13px;
  line-height: 1.6;

  cursor: pointer;
}

.checkbox-declaracao input {
  margin-top: 4px;
  accent-color: #7c3aed;
  flex-shrink: 0;
}

.checkbox-declaracao b {
  color: #d00000;
}

/* AÇÕES */

.acoes-formulario {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 10px;

  margin-top: 35px;
  padding-top: 25px;

  border-top: 1px solid #eee;
}

.acoes-formulario button {
  border: none;
  border-radius: 8px;

  padding: 10px 20px;

  font-size: 14px;
  font-weight: 600;

  cursor: pointer;

  transition:
    background-color 0.15s ease,
    transform 0.1s ease;
}

.acoes-formulario button:active {
  transform: scale(0.98);
}

.btn-voltar {
  background: #e5e5e5;
  color: #333;
}

.btn-voltar:hover {
  background: #d5d5d5;
}

.btn-guardar {
  background: #f1f1f1;
  color: #3a2474;
}

.btn-guardar:hover {
  background: #e5e5e5;
}

.btn-continuar,
.btn-enviar {
  background: #7c3aed;
  color: #fff;
}

.btn-continuar:hover,
.btn-enviar:hover {
  background: #6d28d9;
}

.mensagem-guardado {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 7px;

  margin-top: 15px;

  color: #248a2a;
  font-size: 13px;
  font-weight: 600;
}

/* RESPONSIVIDADE */

@media (max-width: 700px) {
  #parceria-view {
    padding: 24px 5%;
  }

  .cabecalho {
    flex-direction: column;
  }

  .logo {
    width: 150px;
  }

  .formulario {
    padding: 20px;
  }

  .etapa small {
    display: none;
  }

  .acoes-formulario {
    flex-wrap: wrap;
  }

  .acoes-formulario button {
    flex: 1;
    min-width: 130px;
  }
}

@media (max-width: 480px) {
  .acoes-formulario {
    flex-direction: column-reverse;
  }

  .acoes-formulario button {
    width: 100%;
  }

  .cabecalho h1 {
    font-size: 23px;
  }
}
</style>
